/**
 * Le planning, adossé à Supabase.
 *
 * Ce dépôt ne porte qu'une table : `employee_absences`. Le reste du planning —
 * l'équipe, les affectations, les chantiers — se lit là où il vit déjà, et le
 * service les assemble. C'est voulu : recopier ici une lecture d'affectations
 * ferait deux façons de poser la même question, dont une seule serait
 * maintenue.
 */
import type {
  Absence,
  AbsenceKind,
  AbsenceRepository,
  ExecutionContext,
  SaveAbsenceCommand,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const ABSENCE_COLUMNS = `
  id, employee_id, kind, starts_on, ends_on, notes,
  status, decided_at, decision_note,
  employee:employees(first_name, last_name)
`;

const one = (v: unknown): Row | null => (Array.isArray(v) ? (v[0] ?? null) : ((v as Row) ?? null));

const nameOf = (employee: Row | null): string | null => {
  if (!employee) return null;
  const full = [employee.first_name, employee.last_name].filter(Boolean).join(" ").trim();
  return full || null;
};

function toAbsence(row: Row): Absence {
  return {
    id: row.id,
    employeeId: row.employee_id,
    employeeName: nameOf(one(row.employee)),
    kind: row.kind as AbsenceKind,
    startsOn: String(row.starts_on).slice(0, 10),
    endsOn: String(row.ends_on).slice(0, 10),
    notes: row.notes ?? null,
    // `accordee` par défaut : les lignes posées avant le 11 septembre l'ont
    // toutes été par le bureau, qui ne se demande rien à lui-même.
    status: (row.status as Absence["status"]) ?? "accordee",
    decidedAt: row.decided_at ?? null,
    decisionNote: row.decision_note ?? null,
  };
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabasePlanningRepository(
  clientFor: DbClientFactory,
): AbsenceRepository {
  return {
    async listAbsences(ctx: ExecutionContext, filter) {
      let query = clientFor(ctx)
        .from("employee_absences")
        .select(ABSENCE_COLUMNS)
        .order("starts_on", { ascending: true });

      if (filter.employeeId) query = query.eq("employee_id", filter.employeeId);
      // Chevauchement, comme pour les affectations : un congé de trois
      // semaines concerne chacune des trois, et non la seule où il commence.
      // Les deux bornes sont fermées ici — une absence a toujours une fin.
      if (filter.to) query = query.lte("starts_on", filter.to);
      if (filter.from) query = query.gte("ends_on", filter.from);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toAbsence);
    },

    async saveAbsence(ctx: ExecutionContext, cmd: SaveAbsenceCommand) {
      const patch = {
        employee_id: cmd.employeeId,
        kind: cmd.kind,
        starts_on: cmd.startsOn,
        ends_on: cmd.endsOn,
        notes: cmd.notes,
      };

      const query = cmd.absenceId
        ? clientFor(ctx).from("employee_absences").update(patch).eq("id", cmd.absenceId)
        : clientFor(ctx).from("employee_absences").insert(patch);

      const { data, error } = await query.select(ABSENCE_COLUMNS).single();
      if (error) fail(error);
      return toAbsence(data as Row);
    },

    async removeAbsence(ctx: ExecutionContext, absenceId: string) {
      const { error } = await clientFor(ctx)
        .from("employee_absences")
        .delete()
        .eq("id", absenceId);
      if (error) fail(error);
    },

    async myAbsences(ctx: ExecutionContext) {
      // Par la fonction : l'appelant ne connaît pas son `employee_id`, et la
      // policy laisse le bureau lire toute l'entreprise — juste pour le
      // planning, faux sous un intitulé « les miennes ».
      const { data, error } = await clientFor(ctx).rpc("my_absences");
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toAbsence);
    },

    async requestAbsence(ctx: ExecutionContext, cmd) {
      // Par la fonction et non par la table : `managers_write` réserve
      // l'écriture au bureau. La fonction résout le demandeur depuis le compte
      // connecté, donc aucun identifiant de salarié ne circule ici.
      const { data, error } = await clientFor(ctx).rpc("request_absence", {
        p_kind: cmd.kind,
        p_starts_on: cmd.startsOn,
        p_ends_on: cmd.endsOn,
        p_notes: cmd.notes,
      });
      if (error) fail(error);
      return data as string;
    },

    async withdrawRequest(ctx: ExecutionContext, absenceId: string) {
      const { error } = await clientFor(ctx).rpc("withdraw_absence_request", {
        p_id: absenceId,
      });
      if (error) fail(error);
    },

    async decideAbsence(ctx: ExecutionContext, cmd) {
      const { data, error } = await clientFor(ctx).rpc("decide_absence", {
        p_id: cmd.absenceId,
        p_granted: cmd.granted,
        p_note: cmd.note,
      });
      if (error) fail(error);
      // La fonction rend la ligne brute : elle ne porte pas le nom du salarié,
      // que l'écran du patron affiche. On relit par le chemin normal plutôt
      // que de recomposer une demi-absence ici.
      const relu = await this.listAbsences(ctx, {});
      const trouve = relu.find((a) => a.id === (data as Row)?.id);
      return trouve ?? toAbsence(data as Row);
    },
  };
}
